import { FC } from 'react';
import { useQuery } from 'react-query';
import { deepEqual } from 'fast-equals';
import { Loader } from '../../../../components';
import {
  ButtonGroup,
  Button,
  FormControl,
  RadioGroup,
  Radio,
  Box,
  FormControlLabel,
  Divider,
  TextField,
  Grid,
  FormLabel
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { Add, Close, Save } from '@mui/icons-material';
import {
  getNeighborhoodHouseSpecsColorSchemes,
  updateNeighborhoodHouseSpecs,
  getNeighborhoodHouseSpecsPlanFinishLevels
} from '../../../../fetch';
import {
  IDropdownResponse,
  INeighborhoodDetailHouseSpecs,
  INeighborhoodDetailHouseSpecsPut
} from '../../../../models';
import { formatMoney } from '../../../../helpers';

interface IHouseSpecifications {
  houseSpecsData: INeighborhoodDetailHouseSpecs;
  isLoadingHouseSpecs: boolean;
  refetchHouseSpecs: () => void;
}

const HouseSpecificationsSchema = Yup.object().shape({
  isMailboxProvided: Yup.boolean(),
  colorSchemeArea: Yup.string(),
  isIrrigationEnabled: Yup.boolean(),
  irrigationOptionPrice: Yup.string(),
  planFinishLevel: Yup.string()
});

export const HouseSpecifications: FC<IHouseSpecifications> = ({
  houseSpecsData,
  isLoadingHouseSpecs,
  refetchHouseSpecs
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { neighborhoodId }: { neighborhoodId: string } = useParams();

  const history = useHistory();

  const { data: colorSchemes } = useQuery<IDropdownResponse[], Error>(
    ['getNeighborhoodHouseSpecsColorSchemes'],
    () => getNeighborhoodHouseSpecsColorSchemes()
  );

  const { data: placeFinshLevels } = useQuery<IDropdownResponse[], Error>(
    ['getNeighborhoodHouseSpecsPlanFinishLevels'],
    () => getNeighborhoodHouseSpecsPlanFinishLevels()
  );

  return (
    <>
      {isLoadingHouseSpecs && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingHouseSpecs && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            isMailboxProvided: houseSpecsData?.isMailboxProvided ?? null,
            colorSchemeArea: houseSpecsData?.colorSchemeArea ?? null,
            isIrrigationEnabled: houseSpecsData?.isIrrigationEnabled ?? null,
            irrigationOptionPrice: houseSpecsData?.irrigationOptionPrice
              ? formatMoney(houseSpecsData?.irrigationOptionPrice)
              : '$0.00',
            planFinishLevel: houseSpecsData?.planFinishLevel ?? null
          }}
          validationSchema={HouseSpecificationsSchema}
          onSubmit={async (values, actions) => {
            if (neighborhoodId !== 'add') {
              try {
                const data: INeighborhoodDetailHouseSpecsPut = {
                  ...values,
                  irrigationOptionPrice: values.irrigationOptionPrice
                    ? Number(
                        (values.irrigationOptionPrice as string)
                          .replace('$', '')
                          .replaceAll(',', '')
                      )
                    : 0
                };
                await updateNeighborhoodHouseSpecs(neighborhoodId, data);
                await refetchHouseSpecs();
                enqueueSnackbar('Neighborhood has been updated successfully!', {
                  variant: 'success'
                });
              } catch (error) {
                enqueueSnackbar(
                  error?.Detail ?? 'Something went wrong. Neighborhood was not updated.',
                  {
                    variant: 'error'
                  }
                );
              }
            }
          }}
        >
          {({
            isSubmitting,
            values,
            initialValues,
            setFieldValue,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}
                <Prompt
                  when={!deepEqual(initialValues, values)}
                  message="You have unsaved changes, are you sure you want to exit?"
                />
                <Box mt={2}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="outlined" size="small" required>
                        <FormLabel>Mailbox provided by our normal mailbox contractor.</FormLabel>
                        <RadioGroup
                          value={
                            values.isMailboxProvided
                              ? 'Yes'
                              : values.isMailboxProvided === false
                              ? 'No'
                              : null
                          }
                          onChange={(_, val) =>
                            setFieldValue('isMailboxProvided', val === 'Yes' ? true : false)
                          }
                          aria-label="isMailboxProvided"
                          name="isMailboxProvided"
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl variant="outlined" size="small" required>
                        <FormLabel>Enable Irrigation Option</FormLabel>
                        <RadioGroup
                          value={
                            values.isIrrigationEnabled
                              ? 'Yes'
                              : values.isIrrigationEnabled === false
                              ? 'No'
                              : null
                          }
                          onChange={(_, val) =>
                            setFieldValue('isIrrigationEnabled', val === 'Yes' ? true : false)
                          }
                          aria-label="isIrrigationEnabled"
                          name="isIrrigationEnabled"
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                      {values.isIrrigationEnabled && (
                        <TextField
                          fullWidth
                          sx={{ mt: 0.5, display: 'block' }}
                          variant="outlined"
                          autoComplete="nope"
                          label="Irrigation Option Price"
                          name="irrigation"
                          value={values.irrigationOptionPrice}
                          size="small"
                          onBlur={e => {
                            handleBlur(e);
                            setFieldValue('irrigationOptionPrice', formatMoney(e.target.value, 2));
                          }}
                          onChange={e => setFieldValue('irrigationOptionPrice', e.target.value)}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="outlined" size="small" required>
                        <FormLabel>Color Scheme Area</FormLabel>
                        <RadioGroup
                          value={values.colorSchemeArea}
                          onChange={(_, val) => {
                            setFieldValue('colorSchemeArea', val);
                          }}
                          aria-label="color-sheme-area"
                          name="color-sheme-area"
                        >
                          {colorSchemes?.map(scheme => {
                            return (
                              <FormControlLabel
                                key={scheme.value}
                                value={scheme.value}
                                control={<Radio />}
                                label={scheme.description}
                              />
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl variant="outlined" size="small" required>
                        <FormLabel>Plan Finish Level</FormLabel>
                        <RadioGroup
                          value={values.planFinishLevel}
                          onChange={(_, val) => setFieldValue('planFinishLevel', val)}
                          aria-label="planFinishLevel"
                          name="planFinishLevel"
                        >
                          {placeFinshLevels?.map(level => {
                            return (
                              <FormControlLabel
                                key={level.value}
                                value={level.value}
                                control={<Radio />}
                                label={level.description}
                              />
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
                <Box mt={1} mb={0.5}>
                  <Divider />
                </Box>
                <ButtonGroup>
                  <Button
                    disabled={!dirty || isSubmitting || !isValid}
                    type="submit"
                    startIcon={neighborhoodId ? <Save /> : <Add />}
                    variant="contained"
                    color="primary"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="inherit"
                    startIcon={<Close />}
                    onClick={() => {
                      history.push('/development/neighborhoods');
                    }}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};
